import type { LucideIcon } from 'lucide-react';
import { BanknoteIcon, BellIcon, BugIcon, CreditCardIcon, MessageSquareIcon, PhoneForwardedIcon, PhoneIcon, UsersIcon } from 'lucide-react';
import {
	BALANCES_SEARCH_FIELD_OPTIONS,
	BALANCES_SORT_OPTIONS,
	CARDS_SEARCH_FIELD_OPTIONS,
	CARDS_SORT_OPTIONS,
	CLIENTS_SEARCH_FIELD_OPTIONS,
	CLIENTS_SORT_OPTIONS,
	MESSAGES_SEARCH_FIELD_OPTIONS,
	MESSAGES_SORT_OPTIONS,
	PHONES_SEARCH_FIELD_OPTIONS,
	PHONES_SORT_OPTIONS,
	DEVICE_LOGS_SEARCH_FIELD_OPTIONS,
	DEVICE_LOGS_SORT_OPTIONS,
	PUSHES_SEARCH_FIELD_OPTIONS,
	PUSHES_SORT_OPTIONS,
	USSD_SEARCH_FIELD_OPTIONS,
	USSD_SORT_OPTIONS,
	type ClientsListType,
} from '@/entities/clients-filter.entity';

export type FilterSchema = 'client' | 'balance' | 'card' | 'phone' | 'message' | 'push' | 'deviceLog' | 'ussd';

export interface CategoryDef {
	id: ClientsListType;
	label: string;
	icon: LucideIcon;
	/** false = show tab but list is ComingSoon */
	ready: boolean;
}

export const CLIENTS_CATEGORIES: CategoryDef[] = [
	{ id: 'clients', label: 'Клиенты', icon: UsersIcon, ready: true },
	{ id: 'messages', label: 'Сообщения', icon: MessageSquareIcon, ready: true },
	{ id: 'balances', label: 'Балансы', icon: BanknoteIcon, ready: true },
	{ id: 'pushes', label: 'Push', icon: BellIcon, ready: true },
	{ id: 'deviceLogs', label: 'Ошибки', icon: BugIcon, ready: true },
	{ id: 'ussd', label: 'USSD', icon: PhoneForwardedIcon, ready: true },
	{ id: 'cards', label: 'Карты', icon: CreditCardIcon, ready: true },
	{ id: 'phones', label: 'Номера', icon: PhoneIcon, ready: true },
];

export interface CategoryQueryConfig {
	searchFields: { value: string; label: string }[];
	sortFields: { value: string; label: string }[];
	filterSchema: FilterSchema;
	defaultSearchField: string;
	defaultSortBy: string;
}

export const CATEGORY_QUERY_CONFIG: Record<ClientsListType, CategoryQueryConfig> = {
	messages: {
		searchFields: MESSAGES_SEARCH_FIELD_OPTIONS,
		sortFields: MESSAGES_SORT_OPTIONS,
		filterSchema: 'message',
		defaultSearchField: 'sender',
		defaultSortBy: 'createdAt',
	},
	pushes: {
		searchFields: PUSHES_SEARCH_FIELD_OPTIONS,
		sortFields: PUSHES_SORT_OPTIONS,
		filterSchema: 'push',
		defaultSearchField: 'packageName',
		defaultSortBy: 'createdAt',
	},
	deviceLogs: {
		searchFields: DEVICE_LOGS_SEARCH_FIELD_OPTIONS,
		sortFields: DEVICE_LOGS_SORT_OPTIONS,
		filterSchema: 'deviceLog',
		defaultSearchField: 'message',
		defaultSortBy: 'occurredAt',
	},
	ussd: {
		searchFields: USSD_SEARCH_FIELD_OPTIONS,
		sortFields: USSD_SORT_OPTIONS,
		filterSchema: 'ussd',
		defaultSearchField: 'operator',
		defaultSortBy: 'createdAt',
	},
	clients: {
		searchFields: CLIENTS_SEARCH_FIELD_OPTIONS,
		sortFields: CLIENTS_SORT_OPTIONS,
		filterSchema: 'client',
		defaultSearchField: 'id',
		defaultSortBy: 'createdAt',
	},
	balances: {
		searchFields: BALANCES_SEARCH_FIELD_OPTIONS,
		sortFields: BALANCES_SORT_OPTIONS,
		filterSchema: 'balance',
		defaultSearchField: 'clientId',
		defaultSortBy: 'createdAt',
	},
	cards: {
		searchFields: CARDS_SEARCH_FIELD_OPTIONS,
		sortFields: CARDS_SORT_OPTIONS,
		filterSchema: 'card',
		defaultSearchField: 'cardNumber',
		defaultSortBy: 'createdAt',
	},
	phones: {
		searchFields: PHONES_SEARCH_FIELD_OPTIONS,
		sortFields: PHONES_SORT_OPTIONS,
		filterSchema: 'phone',
		defaultSearchField: 'number',
		defaultSortBy: 'createdAt',
	},
};

export function getCategoryConfig(listType: ClientsListType): CategoryQueryConfig {
	return CATEGORY_QUERY_CONFIG[listType];
}

export const DEFAULT_CLIENTS_CATEGORY_ORDER: ClientsListType[] = CLIENTS_CATEGORIES.map(c => c.id);

const CATEGORY_BY_ID = new Map(CLIENTS_CATEGORIES.map(c => [c.id, c]));

export function normalizeCategoryOrder(order: unknown): ClientsListType[] {
	if (!Array.isArray(order)) return [...DEFAULT_CLIENTS_CATEGORY_ORDER];

	const seen = new Set<ClientsListType>();
	const normalized: ClientsListType[] = [];

	for (const item of order) {
		if (typeof item !== 'string') continue;
		if (!CATEGORY_BY_ID.has(item as ClientsListType)) continue;
		const id = item as ClientsListType;
		if (seen.has(id)) continue;
		seen.add(id);
		normalized.push(id);
	}

	for (const cat of CLIENTS_CATEGORIES) {
		if (!seen.has(cat.id)) normalized.push(cat.id);
	}

	return normalized;
}

export function getOrderedCategories(order: ClientsListType[]): CategoryDef[] {
	const normalized = normalizeCategoryOrder(order);
	return normalized.map(id => CATEGORY_BY_ID.get(id)!);
}
